import {
  buildPageTree,
  catalogTeamForRecords,
  findTreeNode,
  HOME_SLUG,
  recordsForTeam,
} from '@/components/pages/pageTree';
import {
  isLayoutNode,
  normalizeLayoutOrders,
} from '@/components/pages/layoutModel';
import { persistRequestNode } from '@/components/pages/requestNodes';

/** Sequential layout orders for every grid on the page, by child id. */
export function layoutOrderPatches(state, pageId) {
  const patches = new Map();
  Object.values(state.nodesById)
    .filter((node) => node.pageId === pageId && isLayoutNode(node))
    .forEach((layout) => {
      const children = (state.childrenByParent[layout.id] || [])
        .map((id) => state.nodesById[id])
        .filter(Boolean);
      normalizeLayoutOrders(children, layout.props?.tracks).forEach(
        (next, id) => patches.set(id, next)
      );
    });
  return patches;
}

export default {
  loading: (state) => state.loading,
  saving: (state) => state.saving,
  treeLoaded: (state) => state.treeLoaded,

  // ============================================
  // Record getters (API-backed page documents)
  // ============================================

  // All records as array
  allRecords: (state) => Object.values(state.recordsById),

  pageTree: (state, _getters, _rootState, rootGetters) => {
    const mainTeamId = rootGetters?.['teams/all']?.find(
      (team) => team.name === 'main_team'
    )?.id;
    return buildPageTree(Object.values(state.recordsById), {
      templatesBySection: state.templatesBySection,
      teamId: rootGetters?.['users/userTeam'] || state.catalogTeamId || '',
      mainTeamId,
    });
  },

  pageBySlug: (state, getters) => (slug) =>
    findTreeNode(getters.pageTree, slug || HOME_SLUG),

  // Get records filtered by location
  recordsByLocation:
    (state, _getters, _rootState, rootGetters) => (location) => {
      const records = Object.values(state.recordsById);
      const mainTeamId = rootGetters?.['teams/all']?.find(
        (team) => team.name === 'main_team'
      )?.id;
      return recordsForTeam(
        records,
        catalogTeamForRecords(
          records,
          rootGetters?.['users/userTeam'] || state.catalogTeamId || '',
          mainTeamId
        )
      ).filter((record) => record.location === location);
    },

  // Get record by ID
  recordById: (state) => (recordId) => state.recordsById[recordId],

  // Get record for a loaded page
  recordForPage: (state) => (pageId) => {
    const page = state.pagesById[pageId];
    if (!page || !page.recordId) return null;
    return state.recordsById[page.recordId];
  },

  // ============================================
  // Page getters (in-memory page content)
  // ============================================

  activePage: (state) => state.pagesById[state.activePageId],

  // Get page by pageId
  pageById: (state) => (pageId) => state.pagesById[pageId],

  nodeById: (state) => (nodeId) => state.nodesById[nodeId],

  childrenOf: (state) => (parentId, pageId) => {
    const key = parentId || `root_${pageId}`;
    return (state.childrenByParent[key] || [])
      .map((id) => state.nodesById[id])
      .filter(Boolean);
  },

  rootNodes: (state, getters) => (pageId) => {
    return getters.childrenOf(null, pageId);
  },

  componentFor: (state) => (nodeType) => {
    return state.registeredComponents[nodeType] || null;
  },

  /**
   * Serialize page back to JSON for persistence.
   * Returns the page content object (goes inside record.page).
   */
  serializePage: (state) => (pageId) => {
    const page = state.pagesById[pageId];
    if (!page) return null;

    const nodes = {};
    const childrenByParent = {};
    const afterIdByNode = {};

    Object.entries(state.childrenByParent).forEach(([, ids]) => {
      (ids || []).forEach((id, index) => {
        afterIdByNode[id] = index === 0 ? null : ids[index - 1];
      });
    });

    Object.values(state.nodesById)
      .filter((n) => n.pageId === pageId)
      .forEach((node) => {
        // Consolidate props for storage efficiency
        const persisted = persistRequestNode(node);
        let props = persisted.props;

        // For markdownStream nodes, join chunks into a single string
        // This prevents storing thousands of single-character entries
        if (
          persisted.nodeType === 'markdownStream' &&
          Array.isArray(props.chunks)
        ) {
          props = {
            ...props,
            chunks: props.chunks.length > 0 ? [props.chunks.join('')] : [],
          };
        }

        nodes[node.id] = {
          id: node.id,
          parentId: node.parentId,
          nodeType: persisted.nodeType,
          props,
          layout: node.layout || null,
          height: node.height || null,
          afterId:
            afterIdByNode[node.id] !== undefined
              ? afterIdByNode[node.id]
              : node.afterId,
          draggable: node.draggable,
          droppable: node.droppable,
        };
      });

    Object.entries(state.childrenByParent)
      .filter(([key]) => key.includes(pageId) || nodes[key])
      .forEach(([key, children]) => {
        childrenByParent[key] = [...children];
      });

    layoutOrderPatches(state, pageId).forEach((layout, id) => {
      if (nodes[id]) nodes[id].layout = layout;
    });

    return {
      pageId,
      id: pageId,
      meta: page.meta,
      nodes,
      childrenByParent,
    };
  },
};
